import { useCallback, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { TraceabilityApiClient } from '../api/TraceabilityApiClient';
import type { TraceWriteRequest } from '../api/TraceabilityRequestType';
import type { TraceResponse } from '../api/TraceabilityResponseType';
import type { Result } from '@/lib/http/HttpClient';
import { JobRequestHandler } from '@/lib/http/JobRequestHandler';
import { useApiCall } from '@/hooks/useApiCall';

export function useTraceSubmission(
  tracabilityApiClient: TraceabilityApiClient,
  traceRef: RefObject<TraceWriteRequest>
): {
  execute: () => Promise<void>;
  cancel: () => void;
  retry: () => Promise<void>;
  result: Result<TraceResponse> | null;
  formSubmitted: boolean;
  setFormSubmitted: (value: boolean) => void;
} {
  const [result, setResult] = useState<Result<TraceResponse> | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const activeHandlerRef = useRef<JobRequestHandler<TraceResponse> | null>(null);

  const apiFn = useCallback(
    (signal: AbortSignal) =>
      new Promise<Result<TraceResponse>>((resolve, reject) => {
        let settled = false;
        const handler = new JobRequestHandler<TraceResponse>({
          initFn: () => tracabilityApiClient.tracewrite(traceRef.current!, { signal }),
          statusFn: (jobId) => tracabilityApiClient.traceStatus({ jobId }, { signal }),
          signal,
          setResult: (res) => {
            if (settled) return;
            settled = true;
            resolve({ ...res, timestamp: new Date().toISOString() } as Result<TraceResponse>);
          },
        });
        activeHandlerRef.current = handler;
        handler.start().catch((err: unknown) => {
          if (settled) return;
          settled = true;
          if ((err instanceof Error || err instanceof DOMException) && err.name === 'AbortError') {
            reject(err);
            return;
          }
          resolve({
            ok: false,
            code: 500,
            name: 'UnknownError',
            message: (err as Error)?.message || 'Failed',
            timestamp: new Date().toISOString(),
          });
        });
      }),
    [tracabilityApiClient, traceRef]
  );

  const { execute: executeCall, retry: retryCall } = useApiCall<TraceResponse>({
    apiFn,
    immediate: false,
    onSuccess: (data) => setResult({ ok: true, data }),
    onError: (message) =>
      setResult({
        ok: false,
        code: 500,
        name: 'UnknownError',
        message,
        timestamp: new Date().toISOString(),
      }),
  });

  const cancel = useCallback(() => {
    activeHandlerRef.current?.cancel();
  }, []);

  const retry = useCallback(async (): Promise<void> => {
    setResult(null);
    await new Promise((resolve) => setTimeout(resolve, 500));
    retryCall();
  }, [retryCall]);

  return {
    execute: executeCall,
    cancel,
    retry,
    result,
    formSubmitted,
    setFormSubmitted,
  };
}
